import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { Modal } from '@/components/modal';
import { Panel } from '@/components/panel';
import { Dropdown } from '@/components/dropdown';

import { Section } from '../common';
import { IntegrationListItem, IntegrationList } from './list';
import { NotionForm } from './notion/notion-form';
import { GoogleDriveForm } from './google-drive/google-drive-form';
import { useSlackIntegration } from './slack/use-slack-integration';

import commonStyles from '../common/common.module.scss';
import styles from './integrations.module.scss';

type IntegrationsSectionProps = { integrations: IntegrationListItem[] };

export const IntegrationsSection = ({ integrations }: IntegrationsSectionProps) => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const integration = searchParams.get('integrate');
    if (integration === 'notion') {
      setShowNotionForm(true);
    } else if (integration === 'google_drive') {
      setShowGoogleDriveForm(true);
    }
  }, [searchParams]);

  const slackConnected = integrations.some((integration) => integration.service === 'SLACK');
  const { slackRedirectUrl, persistSlackState } = useSlackIntegration();
  const [showNotionForm, setShowNotionForm] = useState(false);
  const [showGoogleDriveForm, setShowGoogleDriveForm] = useState(false);

  const integrationActions = [
    {
      icon: <Icon name="Notion" size={16} />,
      label: 'Notion',
      type: 'button' as const,
      onClick: () => setShowNotionForm(true),
    },
    {
      icon: <Icon name="Slack" size={16} />,
      label: 'Slack',
      type: 'link' as const,
      href: slackRedirectUrl.url,
      disabled: slackConnected,
      tooltip: slackConnected
        ? 'Please contact us if you need more than one Slack connection'
        : undefined,
      onClick: persistSlackState,
    },
    {
      icon: <Icon name="GoogleDrive" size={16} />,
      label: 'Google Drive',
      type: 'button' as const,
      onClick: () => setShowGoogleDriveForm(true),
    },
  ];

  return (
    <Section title="Integrations">
      {integrations.length === 0 ? (
        <div className={commonStyles.emptyView}>
          <p>
            Connect your team&apos;s tools to bring together your company&apos;s unstructured
            context.
          </p>
        </div>
      ) : (
        <IntegrationList integrations={integrations} />
      )}

      <Dropdown
        items={integrationActions}
        align="left"
        className={styles.addIntegrationsWrapper}
        menuClassName={styles.integrationsDropdown}
        trigger={(isOpen, setIsOpen) => (
          <Button
            variant="outlined"
            className={styles.addIntegrations}
            onClick={() => setIsOpen(!isOpen)}>
            Add integration
            <Icon name="ChevronDown" />
          </Button>
        )}
      />

      {showNotionForm && (
        <Modal onClose={() => setShowNotionForm(false)}>
          <Panel>
            <NotionForm
              onSuccess={() => setShowNotionForm(false)}
              onClose={() => setShowNotionForm(false)}
            />
          </Panel>
        </Modal>
      )}
      {showGoogleDriveForm && (
        <Modal onClose={() => setShowGoogleDriveForm(false)}>
          <Panel>
            <GoogleDriveForm
              onSuccess={() => setShowGoogleDriveForm(false)}
              onClose={() => setShowGoogleDriveForm(false)}
            />
          </Panel>
        </Modal>
      )}
    </Section>
  );
};
